import { useState, useEffect, useRef } from 'react';
import {
  LANGS,
  getCurrentLang,
  switchLang,
  type LangCode,
} from '@/lib/newtranx';

interface LanguageSwitcherProps {
  /** 导航是否已滚动（决定文字配色，与 Navbar 其它项一致） */
  scrolled: boolean;
}

function LanguageSwitcher({ scrolled }: LanguageSwitcherProps) {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState<LangCode>('zh');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLang(getCurrentLang());
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail === 'zh' || detail === 'tw' || detail === 'en') {
        setLang(detail);
      }
    };
    window.addEventListener('newtranx:langchange', onChange);
    return () => window.removeEventListener('newtranx:langchange', onChange);
  }, []);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const current = LANGS.find((l) => l.code === lang) ?? LANGS[0];

  const handleSelect = (code: LangCode) => {
    setOpen(false);
    if (code !== lang) switchLang(code);
  };

  return (
    // class 含 "xykj" 使插件跳过翻译此切换器（语言名应保持各自原文）。
    <div className="xykj-no-translate relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer
          ${scrolled
            ? 'text-foreground-700 hover:text-primary-600 hover:bg-primary-50/50'
            : 'text-white/85 hover:text-white hover:bg-white/10'
          }`}
      >
        <i className="ri-global-line text-base"></i>
        <span className="xykj-no-translate">{current.label}</span>
        <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${open ? 'rotate-180' : ''}`}></i>
      </button>

      {open && (
        <div className="absolute top-full right-0 pt-2 z-50">
          <div
            className="bg-white rounded-xl border border-background-200 py-2 px-1 min-w-[140px]"
            style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}
          >
            {LANGS.map((l) => (
              <button
                key={l.code}
                type="button"
                onClick={() => handleSelect(l.code)}
                className={`xykj-no-translate w-full text-left px-4 py-2.5 rounded-lg text-sm transition-colors duration-150 cursor-pointer
                  ${l.code === lang
                    ? 'text-primary-600 bg-primary-50 font-medium'
                    : 'text-foreground-800 hover:bg-primary-50'
                  }`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default LanguageSwitcher;
